import { mergeAttributes, Node, NodeConfig } from '@tiptap/core';
import { JSONContent } from '@tiptap/react';
import _ from 'lodash';
import css from './Footnote.module.scss';

export const FOOTNOTE_NAME = 'footnote';
export const FOOTNOTES_SUPERSCRIPT_ID = 'footnotes-superscript';

export const generateReferenceHtmlIdAttribute = (id: string): string =>
  `fnref-${id}`;

export const generateFootnoteHtmlIdAttribute = (id: string): string =>
  `fn-${id}`;

export interface IFootnoteAttributes {
  id: string;
  note: string;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    [FOOTNOTE_NAME]: {
      insertFootnote: () => ReturnType;
      updateFootnote: (
        footnoteId: string,
        newContent: JSONContent
      ) => ReturnType;
      deleteFootnote: (footnoteId: string) => ReturnType;
    };
  }
}

interface IFootnoteOptions {}

export const Footnote = (config: Partial<NodeConfig<IFootnoteOptions>> = {}) =>
  Node.create<IFootnoteOptions>(
    _.extend(
      {
        /**
         * The name of the block. This is used to represent the block in the
         * editor's JSON output.
         * Changing this will break old content!
         * See: https://tiptap.dev/guide/custom-extensions#name
         */
        name: FOOTNOTE_NAME,

        /**
         * The content group this node belongs to.
         * See: https://prosemirror.net/docs/ref/#model.NodeSpec.group
         */
        group: 'inline',

        /**
         * Determines whether this node is an inline node or a block node.
         * See: https://prosemirror.net/docs/ref/#model.NodeSpec.inline
         */
        inline: true,

        /**
         * Can be set to true to indicate that, though this isn't a leaf node,
         * it doesn't have directly editable content and should be treated as a
         * single unit in the view.
         * See: https://prosemirror.net/docs/ref/#model.NodeSpec.atom
         */
        atom: true,

        draggable: true,

        /**
         * Footnote instance attributes. These values are rendered as HTML
         * attributes.
         * See: https://tiptap.dev/guide/custom-extensions#attributes
         */
        addAttributes() {
          return {
            href: {
              parseHTML: (element: HTMLElement): string =>
                element.getAttribute('href') || '',
              renderHTML: (
                attributes: Record<string, string>
              ): Record<string, string> => ({
                href: `#${generateFootnoteHtmlIdAttribute(attributes.id)}`,
              }),
            },
            id: {
              parseHTML: (element: HTMLElement): string =>
                element.getAttribute('data-id'),
              renderHTML: (
                attributes: Record<string, string>
              ): Record<string, string> => ({
                id: generateReferenceHtmlIdAttribute(attributes.id),
              }),
            },
          };
        },

        addCommands() {
          return {
            insertFootnote:
              () =>
              ({ commands }) =>
                commands.insertContent({ type: FOOTNOTE_NAME }),
            updateFootnote:
              (footnoteId: string, newContent: JSONContent) =>
              ({ tr, state }) => {
                let updated = false;
                state.doc.descendants((node, pos) => {
                  if (
                    node.type.name === FOOTNOTE_NAME &&
                    node.attrs?.id === footnoteId
                  ) {
                    const contentNodes = newContent.content
                      ? newContent.content.map((item) =>
                          state.schema.nodeFromJSON(item)
                        )
                      : [];

                    const newNode = node.type.create(
                      { ...node.attrs },
                      contentNodes
                    );

                    tr.replaceWith(pos, pos + node.nodeSize, newNode);
                    updated = true;
                    return false; // Stop traversing
                  }
                });
                return updated;
              },
            deleteFootnote:
              (footnoteId: string) =>
              ({ tr, state }) => {
                let updated = false;
                state.doc.descendants((node, pos) => {
                  if (
                    node.type.name === FOOTNOTE_NAME &&
                    node.attrs?.id === footnoteId
                  ) {
                    tr.delete(pos, pos + node.nodeSize);
                    updated = true;
                    return false; // Stop traversing
                  }
                });
                return updated;
              },
          };
        },

        parseHTML() {
          return [
            {
              tag: 'a',
              attrs: { 'data-type': FOOTNOTE_NAME },
            },
          ];
        },

        renderHTML({ HTMLAttributes }) {
          return [
            'a',
            mergeAttributes(HTMLAttributes, {
              'aria-labelledby': FOOTNOTES_SUPERSCRIPT_ID,
              'data-type': FOOTNOTE_NAME,
            }),
            ['sup', { class: css.sup }],
          ];
        },
      },
      config
    )
  );
